import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import keyBy from 'lodash/keyBy';
import { FaExclamation as WarningIcon } from 'react-icons/fa';

import FormControl from '../formControl.component';
import { CheckboxGroup, Checkbox } from 'react-checkbox-group';
import { debouncedRebuildReactTooltip } from '../../debouncedRebuildReactTooltip';
import { singleAssignmentPropTypes } from '../../propTypes/userClients';

const getMissingDependencies = (permission, enabledPermissions, roleBasedPermissions) =>
  permission.requires?.filter(required =>
    !(enabledPermissions.includes(required) || !!roleBasedPermissions[required])) ?? [];

const hasMissingDependencies = (permission, enabledPermissions, roleBasedPermissions) =>
  !!getMissingDependencies(permission, enabledPermissions, roleBasedPermissions).length;

const getAssignmentPermissionsToRemove = ({ newPermissions, checkedAssignments, availableAssignmentPermissions }) => {
  const availableAssignmentPermissionsMap = keyBy(availableAssignmentPermissions, 'value');
  const assignmentsToRemove = {}; // { [assignmentType: string]: { ... } }
  checkedAssignments.forEach(({ assignmentCode, assignmentType, privileges }) => {
    if (!assignmentsToRemove[assignmentType]) {
      assignmentsToRemove[assignmentType] = {
        selectedAssignments: new Set(),
        assignmentTypeCode: assignmentType,
        newValues: [],
        removedValues: new Set(),
      };
    }
    assignmentsToRemove[assignmentType].selectedAssignments.add(assignmentCode);

    privileges.forEach(privilege => {
      const isAssignmentValid = availableAssignmentPermissionsMap[privilege]?.requiresPermissions
        ? availableAssignmentPermissionsMap[privilege].requiresPermissions
          .every(requiredPermission => newPermissions.includes(requiredPermission))
        : true;
      if (!isAssignmentValid) {
        assignmentsToRemove[assignmentType].removedValues.add(privilege);
      }
    });
  });
  return Object.values(assignmentsToRemove).map(assignment => ({
    ...assignment,
    selectedAssignments: [...assignment.selectedAssignments],
    removedValues: [...assignment.removedValues],
  }));
};

const PermissionsForm = ({
  changePermissions,
  selectedPermissionsFormValue,
  shouldResetValues,
  availablePermissions,
  loggedInUserPermissions = [],
  selectedRole = null,
  selectedClient = null,
  readOnly,
  getPermissionsFunction,
  roleBasedPermissions = {},
  checkedAssignments,
  changeAssignmentsPermissions,
  availableAssignmentPermissions,
}) => {
  const assignablePermissions = useMemo(() => getPermissionsFunction({
    permissions: availablePermissions,
    selectedRole,
    selectedClient,
  }), [availablePermissions, selectedRole, selectedClient, getPermissionsFunction]);

  const assignablePermissionsByValue = useMemo(() => keyBy(assignablePermissions, 'value'), [assignablePermissions]);
  const missingDependencies = assignablePermissions.reduce((totalMissing, assignablePrivilege) => {
    const missing = getMissingDependencies(assignablePrivilege, selectedPermissionsFormValue, roleBasedPermissions);
    if (missing.length) {
      totalMissing[assignablePrivilege.value] = missing.length === 1
        ? `Required permission: "${assignablePermissionsByValue[missing[0]].label}" is not checked`
        : `Required permissions: ${missing.map(value => `"${assignablePermissionsByValue[value].label}"`).join(', ')} are not checked`;
    }
    return totalMissing;
  }, {});

  // unchecking dependent permissions and assignments
  const updatePermission = useCallback(newPermissions => {
    changePermissions(newPermissions.filter(permissionValue =>
      assignablePermissionsByValue[permissionValue] // Workaround to clear not longer valid permissions from user
      && !hasMissingDependencies(assignablePermissionsByValue[permissionValue], newPermissions, roleBasedPermissions)
    ));
    const assignmentsToRemove = getAssignmentPermissionsToRemove({
      newPermissions,
      checkedAssignments,
      availableAssignmentPermissions,
    });
    assignmentsToRemove.forEach(assignmentToRemove => {
      changeAssignmentsPermissions(assignmentToRemove);
    });
  }, [
    changePermissions,
    assignablePermissionsByValue,
    checkedAssignments,
    availableAssignmentPermissions,
    changeAssignmentsPermissions,
    roleBasedPermissions,
  ]);

  useEffect(debouncedRebuildReactTooltip, [missingDependencies]);

  if (loggedInUserPermissions.length > 0) {
    assignablePermissions.forEach(item => {
      if (!loggedInUserPermissions.includes(item.value)) {
        item.disabled = true;
      }
    });
  }

  return (
    <FormControl
      onChange={updatePermission}
      name="permissions"
      label="Permissions"
      value={selectedPermissionsFormValue}
      shouldReset={shouldResetValues}
      hideLabel
    >
      <CheckboxGroup
        checkboxDepth={3}
      >
        <div className="row">
          {
            assignablePermissions.map(({ disabled, label, value }) => (
              <label
                key={value}
                className="col-6 d-flex align-items-baseline"
                data-tip={missingDependencies[value] ?? ''}
              >
                <Checkbox
                  className="mr-2"
                  value={value}
                  disabled={readOnly
                    || disabled
                    || roleBasedPermissions[value]
                    || !selectedClient
                    || !selectedRole
                    || !!missingDependencies[value]
                  }
                />
                <div className="dependency-indicator mr-1">
                  {!!missingDependencies[value] && <WarningIcon/> }
                </div>
                <div className="from-role-indicator mr-1">
                  {
                    roleBasedPermissions[value] ? '(Role)': ''
                  }
                </div>
                {label}
              </label>
            ))
          }
        </div>
      </CheckboxGroup>
    </FormControl>
  );
};

PermissionsForm.propTypes = {
  changePermissions: PropTypes.func.isRequired,
  selectedPermissionsFormValue: PropTypes.arrayOf(PropTypes.string).isRequired,
  shouldResetValues: PropTypes.bool.isRequired,
  availablePermissions: PropTypes.arrayOf(PropTypes.shape({
    availablePermissions: PropTypes.shape,
  })).isRequired,
  loggedInUserPermissions: PropTypes.shape({}),
  selectedRole: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }),
  selectedClient: PropTypes.shape({
    clientId: PropTypes.number.isRequired,
  }),
  readOnly: PropTypes.bool.isRequired,
  getPermissionsFunction: PropTypes.func.isRequired,
  roleBasedPermissions: PropTypes.shape({}),
  checkedAssignments: PropTypes.arrayOf(singleAssignmentPropTypes).isRequired,
  changeAssignmentsPermissions: PropTypes.func.isRequired,
  availableAssignmentPermissions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    requiresPermissions: PropTypes.arrayOf(PropTypes.string),
  })).isRequired,
};

export default PermissionsForm;
